import { IValidationRule } from '../constants/interfaces';
import { IFormStateValue } from './formUtils';
import { fetchPayoffAmount } from '../api/client';

interface ApiRule {
  apiCall: string;
  condition: string;
  canContinue?: boolean;
  message: string;
  shouldReset?: boolean;
  title?: string;
  accountIdField?: string; // Field ID that contains the account ID
}

interface ApiValidationResult {
  isValid: boolean;
  error?: IValidationRule;
  isLoading?: boolean;
}

/**
 * Validates API-based rules that require external data
 * @param formState - Current form state
 * @param rule - API rule to validate
 * @returns Promise with validation result
 */
export async function validateApiRule(
  formState: Record<string, IFormStateValue>,
  rule: ApiRule
): Promise<ApiValidationResult> {
  try {
    const apiData: Record<string, unknown> = {};

    // Handle different API calls
    if (rule.apiCall === 'payoff_amount') {
      // Extract account ID from the specified field or try to find it
      let accountIdField = rule.accountIdField;

      if (!accountIdField) {
        // Try to find a field that looks like an account ID (common field IDs: 2, account_id, etc.)
        accountIdField = Object.keys(formState).find((key) => {
          const value = formState[key]?.value;
          return value && typeof value === 'string' && value.trim().length > 0;
        });
      }

      if (!accountIdField || !formState[accountIdField]?.value) {
        return {
          error: {
            canContinue: false,
            message: 'Account ID is required for payoff amount validation',
            shouldReset: false,
            title: 'Missing Account ID',
          },
          isValid: false,
        };
      }

      const accountId = String(formState[accountIdField].value).trim();

      try {
        const response = await fetchPayoffAmount(accountId);
        apiData.payoff_amount = response.data.payoffAmount;
      } catch (error: unknown) {
        console.error('Failed to fetch payoff amount:', error);

        // Extract specific error message from API response
        let errorMessage =
          'Unable to verify payoff amount. Please ensure the account ID is correct and try again.';
        let title = 'Payoff Amount Verification Failed';

        const apiError = error as {
          response?: { data?: string; status?: number };
          message?: string;
        };
        if (apiError?.response?.data) {
          // If error response contains a string message, use it
          if (typeof apiError.response.data === 'string') {
            errorMessage = apiError.response.data;
          }
          // Handle specific error status codes with more user-friendly messages
          switch (apiError?.response?.status) {
            case 400:
              title = 'Invalid Request';
              break;
            case 404:
              title = 'Account Not Found';
              break;
            case 500:
              title = 'Server Error';
              break;
            default:
              title = 'Payoff Amount Verification Failed';
          }
        } else if (apiError?.message) {
          errorMessage = apiError.message;
        }

        return {
          error: {
            canContinue: true,
            message: errorMessage,
            shouldReset: false,
            showAsToast: true, // Add flag to indicate this should be shown as toast
            title: title,
          },
          isValid: false,
        };
      }
    }

    // Extract field variables from condition (e.g., value123)
    const variableNames = Array.from(
      rule.condition.matchAll(/\bvalue\d+\b/g)
    ).map((m) => (m as RegExpMatchArray)[0]);
    // Extract API variables from condition (e.g., payoff_amount)
    const apiVariableNames = Object.keys(apiData);

    // Prepare arguments for function evaluation
    const argList = [...variableNames, ...apiVariableNames];
    const values = [
      ...variableNames.map((name) => {
        const fieldNum = parseInt(name.replace('value', ''), 10);
        return formState[String(fieldNum)]?.value;
      }),
      ...apiVariableNames.map((name) => apiData[name]),
    ];

    // Create and execute the validation function
    // eslint-disable-next-line no-new-func
    const fn = new Function(...argList, `return ${rule.condition};`);
    const result = fn(...values);

    if (result) {
      return {
        error: {
          canContinue: rule.canContinue ?? false,
          message: rule.message,
          shouldReset: rule.shouldReset ?? false,
          title: rule.title,
        },
        isValid: false,
      };
    }

    return { isValid: true };
  } catch (error) {
    console.error('Error evaluating API rule:', error);
    return {
      error: {
        canContinue: false,
        message: 'An error occurred during validation. Please try again.',
        shouldReset: false,
        title: 'Validation Error',
      },
      isValid: false,
    };
  }
}

/**
 * Validates multiple API rules sequentially
 * @param formState - Current form state
 * @param rules - Array of API rules to validate
 * @returns Promise with validation result
 */
export async function validateApiRules(
  formState: Record<string, IFormStateValue>,
  rules: ApiRule[] = []
): Promise<ApiValidationResult> {
  for (const rule of rules) {
    const result = await validateApiRule(formState, rule);
    if (!result.isValid) {
      return result;
    }
  }

  return { isValid: true };
}
